import { fetchApi } from '@/requests/api';
import { BASE_DASHBOARD_URL } from '@/requests/constants';
import {
  useMutation,
  useQueryClient,
  type UseMutationOptions,
} from '@tanstack/react-query';
import { WORK_ENGAGEMENT_BOOKMARK_QUERY_KEY } from './use-get-user-bookmarks';
import type {
  Bookmark,
  Folder,
  WorkEngagementBookmarks,
  WorkEngagementBookmarksArray,
} from '../types';
import { WorkEngagementBookmarksArraySchema } from '../schemas/user-bookmark-schemas';
import {
  addBookmarkToLocation,
  addFolderToLocation,
  type FolderIndexPath,
} from '../utils/bookmark-utils';
import { nanoid } from 'nanoid';

interface ShareFolderToWorkEngagementVariables {
  readonly folder: Folder;
  readonly workEngagementIds: readonly string[];
}

interface WorkEngagementShareFailure {
  readonly workEngagementId: string;
  readonly errorMessage: string;
}

interface ShareFolderToWorkEngagementResult {
  readonly succeededWorkEngagementIds: readonly string[];
  readonly failedWorkEngagements: readonly WorkEngagementShareFailure[];
}

function cloneFolderWithNewClientIds(folder: Folder): Folder {
  return {
    ...folder,
    client_id: nanoid(),
    child_bookmarks: (folder.child_bookmarks ?? []).map((bookmark) => ({
      ...bookmark,
      client_id: nanoid(),
    })),
    child_folders: (folder.child_folders ?? []).map(
      cloneFolderWithNewClientIds
    ),
  };
}

async function getWorkEngagementBookmarksById(
  workEngagementId: string
): Promise<WorkEngagementBookmarks | null> {
  const res = await fetchApi({
    url: `${BASE_DASHBOARD_URL}/bookmarks/workengagement?work_engagement_ids=${workEngagementId}`,
    method: 'GET',
  });

  if (res.status === 404) {
    return null;
  }

  if (!res.ok) {
    throw new Error(`Failed to fetch work engagement bookmarks: ${res.status}`);
  }

  const parsed = WorkEngagementBookmarksArraySchema.safeParse(await res.json());
  if (!parsed.success) {
    throw new Error('Invalid work engagement bookmarks data');
  }

  return parsed.data[0] ?? null;
}

export function useSaveWorkEngagementBookmarks(
  workEngagementId: string | null,
  options?: UseMutationOptions<
    WorkEngagementBookmarks,
    Error,
    Omit<WorkEngagementBookmarks, 'work_engagement_id' | 'customer_id'>
  >
) {
  const queryClient = useQueryClient();
  return useMutation({
    ...options,
    mutationFn: async (bookmark) => {
      if (!workEngagementId) {
        throw new Error('Work engagement ID is required');
      }

      const updatedBookmarkWithWorkEngagementId = {
        ...bookmark,
        work_engagement_id: workEngagementId,
      };

      const res = await fetchApi({
        url: `${BASE_DASHBOARD_URL}/bookmarks/workengagement`,
        method: 'PUT',
        body: JSON.stringify(updatedBookmarkWithWorkEngagementId),
      });
      if (!res.ok) {
        throw new Error('Failed to create bookmark');
      }
      const data = await res.json();
      return data;
    },
    mutationKey: [
      WORK_ENGAGEMENT_BOOKMARK_QUERY_KEY,
      workEngagementId ? [workEngagementId] : [],
    ],
    onMutate: async (variables) => {
      await queryClient.cancelQueries({
        queryKey: [
          WORK_ENGAGEMENT_BOOKMARK_QUERY_KEY,
          workEngagementId ? [workEngagementId] : [],
        ],
      });

      const previousBookmarks =
        queryClient.getQueryData<WorkEngagementBookmarksArray>([
          WORK_ENGAGEMENT_BOOKMARK_QUERY_KEY,
          workEngagementId ? [workEngagementId] : [],
        ]);

      queryClient.setQueryData(
        [
          WORK_ENGAGEMENT_BOOKMARK_QUERY_KEY,
          workEngagementId ? [workEngagementId] : [],
        ],
        [variables]
      );
      return { previousBookmarks };
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: [
          WORK_ENGAGEMENT_BOOKMARK_QUERY_KEY,
          workEngagementId ? [workEngagementId] : [],
        ],
      });
    },
    onError: (_err, _variables, context) => {
      // Restore from context
      if (context?.previousBookmarks) {
        queryClient.setQueryData(
          [
            WORK_ENGAGEMENT_BOOKMARK_QUERY_KEY,
            workEngagementId ? [workEngagementId] : [],
          ],
          context.previousBookmarks
        );
      }
    },
  });
}

export function useSaveWorkEngagementBookmark(workEngagementId: string | null) {
  const queryClient = useQueryClient();
  return useMutation<
    WorkEngagementBookmarks,
    Error,
    Omit<Bookmark, 'order'> & {
      folderPath?: FolderIndexPath;
    }
  >({
    mutationFn: async ({ folderPath, ...bookmark }) => {
      if (!workEngagementId) {
        throw new Error('Work engagement ID is required');
      }

      const workEngagementBookmarks =
        queryClient.getQueryData<WorkEngagementBookmarksArray>([
          WORK_ENGAGEMENT_BOOKMARK_QUERY_KEY,
          [workEngagementId],
        ]);

      const updatedBookmarks = addBookmarkToLocation(
        workEngagementBookmarks?.[0],
        bookmark,
        folderPath,
        'workengagement'
      );

      const updatedBookmarksWithWorkEngagementId = {
        ...updatedBookmarks,
        work_engagement_id: workEngagementId,
      };

      const res = await fetchApi({
        url: `${BASE_DASHBOARD_URL}/bookmarks/workengagement`,
        method: 'PUT',
        body: JSON.stringify(updatedBookmarksWithWorkEngagementId),
      });
      if (!res.ok) {
        throw new Error('Failed to create bookmark');
      }
      const data = await res.json();
      return data;
    },
    onSuccess: (data) => {
      queryClient.setQueryData(
        [
          WORK_ENGAGEMENT_BOOKMARK_QUERY_KEY,
          workEngagementId ? [workEngagementId] : [],
        ],
        [data]
      );
      queryClient.invalidateQueries({
        queryKey: [
          WORK_ENGAGEMENT_BOOKMARK_QUERY_KEY,
          workEngagementId ? [workEngagementId] : [],
        ],
      });
    },
  });
}

export function useSaveWorkEngagementFolder(workEngagementId: string | null) {
  const queryClient = useQueryClient();
  return useMutation<
    WorkEngagementBookmarks,
    Error,
    Folder & {
      folderPath?: FolderIndexPath;
    }
  >({
    mutationFn: async ({ folderPath, ...folder }) => {
      if (!workEngagementId) {
        throw new Error('Work engagement ID is required');
      }

      const workEngagementBookmarks =
        queryClient.getQueryData<WorkEngagementBookmarksArray>([
          WORK_ENGAGEMENT_BOOKMARK_QUERY_KEY,
          [workEngagementId],
        ]);

      const updatedBookmarks = addFolderToLocation(
        workEngagementBookmarks?.[0],
        folder,
        folderPath,
        'workengagement'
      );

      const updatedBookmarksWithWorkEngagementId = {
        ...updatedBookmarks,
        work_engagement_id: workEngagementId,
      };

      const res = await fetchApi({
        url: `${BASE_DASHBOARD_URL}/bookmarks/workengagement`,
        method: 'PUT',
        body: JSON.stringify(updatedBookmarksWithWorkEngagementId),
      });
      if (!res.ok) {
        throw new Error('Failed to create folder');
      }
      const data = await res.json();
      return data;
    },
    onSuccess: (data) => {
      queryClient.setQueryData(
        [
          WORK_ENGAGEMENT_BOOKMARK_QUERY_KEY,
          workEngagementId ? [workEngagementId] : [],
        ],
        [data]
      );
      queryClient.invalidateQueries({
        queryKey: [
          WORK_ENGAGEMENT_BOOKMARK_QUERY_KEY,
          workEngagementId ? [workEngagementId] : [],
        ],
      });
    },
  });
}

export function useShareBookmarksFolderToWorkEngagements() {
  const queryClient = useQueryClient();

  return useMutation<
    ShareFolderToWorkEngagementResult,
    Error,
    ShareFolderToWorkEngagementVariables
  >({
    mutationFn: async ({ folder, workEngagementIds }) => {
      const results = await Promise.all(
        workEngagementIds.map(async (workEngagementId) => {
          try {
            const currentWorkEngagementBookmarks =
              await getWorkEngagementBookmarksById(workEngagementId);

            const folderToInsert = cloneFolderWithNewClientIds(folder);

            const workEngagementBookmarksWithFolder = addFolderToLocation(
              currentWorkEngagementBookmarks ?? undefined,
              folderToInsert,
              [],
              'workengagement'
            );

            const response = await fetchApi({
              url: `${BASE_DASHBOARD_URL}/bookmarks/workengagement`,
              method: 'PUT',
              body: JSON.stringify({
                ...workEngagementBookmarksWithFolder,
                work_engagement_id: workEngagementId,
              }),
            });

            if (!response.ok) {
              return {
                status: 'failed' as const,
                workEngagementId,
                errorMessage: `HTTP ${response.status}`,
              };
            }

            const data = (await response.json()) as WorkEngagementBookmarks;

            queryClient.setQueryData(
              [WORK_ENGAGEMENT_BOOKMARK_QUERY_KEY, [workEngagementId]],
              [data]
            );

            return {
              status: 'success' as const,
              workEngagementId,
            };
          } catch (error) {
            return {
              status: 'failed' as const,
              workEngagementId,
              errorMessage:
                error instanceof Error ? error.message : 'Unknown error',
            };
          }
        })
      );

      const succeededWorkEngagementIds = results
        .filter((result) => result.status === 'success')
        .map((result) => result.workEngagementId);
      const failedWorkEngagements = results
        .filter((result) => result.status === 'failed')
        .map((result) => ({
          workEngagementId: result.workEngagementId,
          errorMessage: result.errorMessage,
        }));

      return { succeededWorkEngagementIds, failedWorkEngagements };
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: [WORK_ENGAGEMENT_BOOKMARK_QUERY_KEY],
      });
    },
  });
}
